import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Mail, MailOpen, MousePointer, XCircle, AlertTriangle, SendHorizontal } from "lucide-react";
import { emailApi, EmailLogEntry } from "@/services/api/emailApi";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface EmailHistoryTabProps {
  orderId: number;
}

export const EmailHistoryTab = ({ orderId }: EmailHistoryTabProps) => {
  const { toast } = useToast();
  const [selectedEmail, setSelectedEmail] = useState<EmailLogEntry | null>(null);
  const [isResending, setIsResending] = useState(false);

  const { data: emails, isLoading: isLoadingEmails, error, refetch } = useQuery({
    queryKey: ['order-emails', orderId],
    queryFn: () => emailApi.getEntityEmails('orders', orderId),
    meta: {
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "Error loading email history",
          description: error.message || "Failed to fetch email history"
        });
      }
    }
  });

  const getStatusBadge = (email: EmailLogEntry) => {
    if (email.spam_reported_at) {
      return <Badge variant="destructive" className="ml-2">Spam Reported</Badge>;
    } else if (email.bounced_at) {
      return <Badge variant="destructive" className="ml-2">Bounced</Badge>;
    } else if (email.clicked_at) {
      return <Badge variant="success" className="ml-2">Clicked</Badge>;
    } else if (email.opened_at) {
      return <Badge variant="outline" className="ml-2">Opened</Badge>;
    } else if (email.delivered_at) {
      return <Badge variant="secondary" className="ml-2">Delivered</Badge>;
    } else {
      return <Badge variant="outline" className="ml-2">Sent</Badge>;
    }
  };

  const getStatusIcon = (email: EmailLogEntry) => {
    if (email.spam_reported_at) {
      return <AlertTriangle className="h-4 w-4 text-red-500" />;
    } else if (email.bounced_at) {
      return <XCircle className="h-4 w-4 text-red-500" />;
    } else if (email.clicked_at) {
      return <MousePointer className="h-4 w-4 text-green-500" />;
    } else if (email.opened_at) {
      return <MailOpen className="h-4 w-4 text-blue-500" />;
    } else {
      return <Mail className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatDate = (dateString?: string | Date | null) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(date);
  };

  const handleResendEmail = async (email: EmailLogEntry) => {
    if (!email || isResending) return;
    
    try {
      setIsResending(true);
      
      const resendData: EmailLogEntry = {
        customer_id: email.customer_id,
        to_email: email.to_email,
        from_email: email.from_email,
        subject: `[Resent] ${email.subject}`,
        content: email.content,
        entity_name: email.entity_name,
        entity_id: email.entity_id
      };
      
      await emailApi.sendAndLogEmail(resendData);
      
      toast({
        title: "Email resent successfully",
        description: `Email has been resent to ${email.to_email}`,
      });
      
      refetch();
    } catch (error: any) {
      console.error("Error resending email:", error);
      toast({
        variant: "destructive",
        title: "Failed to resend email",
        description: error.message || "An error occurred while resending the email"
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Email History ({emails?.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoadingEmails ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-500">Failed to load email history</p>
              <Button onClick={() => refetch()} className="mt-4">
                Try Again
              </Button>
            </div>
          ) : !emails || emails.length === 0 ? (
            <div className="text-center py-8">
              <Mail className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium">No email history</h3>
              <p className="text-sm text-muted-foreground mt-2">
                No emails have been sent for this order yet
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]"></TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Sent Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {emails?.map((email) => (
                  <TableRow key={email.id}>
                    <TableCell>{getStatusIcon(email)}</TableCell>
                    <TableCell className="font-medium">{email.subject}</TableCell>
                    <TableCell>{email.to_email}</TableCell>
                    <TableCell>{formatDate(email.sent_at)}</TableCell>
                    <TableCell>{getStatusBadge(email)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          disabled={isResending}
                          onClick={() => handleResendEmail(email)}
                          title="Resend Email"
                        >
                          <SendHorizontal className={`h-4 w-4 ${isResending ? 'animate-pulse' : ''}`} />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setSelectedEmail(email)}
                        >
                          View
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selectedEmail} onOpenChange={(open) => !open && setSelectedEmail(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{selectedEmail?.subject}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <p className="text-sm font-medium">From:</p>
              <p className="col-span-3">{selectedEmail?.from_email}</p>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <p className="text-sm font-medium">To:</p>
              <p className="col-span-3">{selectedEmail?.to_email}</p>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <p className="text-sm font-medium">Sent:</p>
              <p className="col-span-3">{formatDate(selectedEmail?.sent_at)}</p>
            </div>
            
            <div className="my-4">
              <p className="text-sm font-medium mb-2">Email Status:</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Sent</span>
                  <span>{formatDate(selectedEmail?.sent_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Delivered</span>
                  <span>{formatDate(selectedEmail?.delivered_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Opened</span>
                  <span>{formatDate(selectedEmail?.opened_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Clicked</span>
                  <span>{formatDate(selectedEmail?.clicked_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Bounced</span>
                  <span>{formatDate(selectedEmail?.bounced_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Spam Reported</span>
                  <span>{formatDate(selectedEmail?.spam_reported_at)}</span>
                </div>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-2">Email Content:</p>
              <div className="border rounded p-4 max-h-96 overflow-auto">
                <div dangerouslySetInnerHTML={{ __html: selectedEmail?.content || '' }} />
              </div>
            </div>
            
            <div className="flex justify-end mt-4">
              <Button 
                variant="outline"
                onClick={() => {
                  if (selectedEmail) {
                    handleResendEmail(selectedEmail);
                  }
                }}
                disabled={isResending}
                className="flex items-center gap-2"
              >
                <SendHorizontal className="h-4 w-4" />
                {isResending ? 'Sending...' : 'Resend Email'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
